import {
  AfterViewInit,
  Component,
  HostListener,
  Inject,
  Input,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {
  EVENT_CALENDAR_ROUTE,
  EVENT_DETAILS, FINISHED_EVENTS_ROUTE, PRIVATE_EVENT_CALENDAR_ROUTE, PRIVATE_FINISHED_EVENTS_ROUTE
} from "src/app/app-routing.module";
import {environment} from "@environment";
import {AbstractComponent} from "@components";
import {
  EventItem, Lookups
} from "@model";
import {SwiperComponent} from "swiper/angular";
import {Subject} from "rxjs";
import {isPlatformBrowser} from "@angular/common";
import {AuthenticationService} from '@services';

@Component({
  selector: 'app-landing',
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss']
})
export class LandingComponent extends AbstractComponent implements AfterViewInit {

  @Input() isLanding: boolean = false;
  @ViewChild('upcomingEventsSwiper', {static: false}) upcomingEventsSwiper?: SwiperComponent;
  @ViewChild('newEventsSwiper', {static: false}) newEventsSwiper?: SwiperComponent;

  @Input() upcomingItems: EventItem[] = [];
  @Input() newItems: EventItem[] = [];
  @Input() countries: Lookups[] = [];
  @Input({required: true}) languages: Lookups[] = [];

  searchQuery = new Subject();
  page: number = 1;
  environment: any = environment;
  breakpoints = {
    1400: {slidesPerView: 3, spaceBetween: 0},
    992: {slidesPerView: 3, spaceBetween: 0},
    576: {slidesPerView: 2, spaceBetween: 0},
    320: {slidesPerView: 1.05, spaceBetween: 10}
  };

  public currentWindowWidth: number = 0;

  sports = [
    {id: 30, imgSrc: 'assets/icons/icons-alt/running_road.svg', buttonTextKey: 'app.common.sports.30'},
    {id: 36, imgSrc: 'assets/icons/icons-alt/trail_running.svg', buttonTextKey: 'app.common.sports.36'},
    {id: 35, imgSrc: 'assets/icons/icons-alt/swimming.svg', buttonTextKey: 'app.common.sports.35'},
    {id: undefined, imgSrc: '', buttonTextKey: 'app.common.sports.43'}
  ];

  isBrowser: boolean;

  swiperStates = {
    upcoming: {currentSlide: 1, totalSlides: 1},
    newEvents: {currentSlide: 1, totalSlides: 1}
  };

  constructor(private router: Router, private route: ActivatedRoute, @Inject(PLATFORM_ID) private platformId: Object, public authenticationService: AuthenticationService
  ) {
    super();
    this.isBrowser = isPlatformBrowser(this.platformId);

    if (this.isBrowser) {
      this.currentWindowWidth = window.innerWidth;
    }
  }

  ngAfterViewInit(): void {
    this.updateSlideCounter(this.upcomingEventsSwiper, 'upcoming');
    this.updateSlideCounter(this.newEventsSwiper, 'newEvents');
  }

  @HostListener('window:resize')
  onResize(): void {
    this.currentWindowWidth = window.innerWidth;
  }

  goToEventDetailsPage(item: EventItem) {
    this.router.navigate([EVENT_DETAILS], {
      relativeTo: this.route,
      queryParams: {eventId: item.id}
    });
  }

  trackByFn(_: number, item: EventItem) {
    return item.id;
  }

  exploreAll() {
    this.router.navigate([EVENT_CALENDAR_ROUTE]);
  }

  slideNext(swiperComponent: SwiperComponent | undefined, key: keyof typeof this.swiperStates) {
    if (!swiperComponent || this.isLastSlideActive(swiperComponent)) return;
    swiperComponent.swiperRef.slideNext(300);
    this.updateSlideCounter(swiperComponent, key);
  }

  slidePrev(swiperComponent: SwiperComponent | undefined, key: keyof typeof this.swiperStates) {
    if (!swiperComponent || this.isFirstSlideActive(swiperComponent)) return;
    swiperComponent.swiperRef.slidePrev(300);
    this.updateSlideCounter(swiperComponent, key);
  }

  isLastSlideActive(swiper?: SwiperComponent): boolean {
    if (!swiper || !swiper.swiperRef) return false;

    const swiperInstance = swiper.swiperRef;

    const slidesPerView = this.getCurrentSlidesPerView();

    const lastVisibleIndex = swiperInstance.slides.length - slidesPerView;

    return swiperInstance.activeIndex >= lastVisibleIndex;
  }

  getCurrentSlidesPerView(): number {
    if (this.currentWindowWidth >= 1400) {
      return 3;
    } else if (this.currentWindowWidth >= 992) {
      return 3;
    } else if (this.currentWindowWidth >= 576) {
      return 2;
    } else {
      return 1.5;
    }
  }

  isFirstSlideActive(swiper?: SwiperComponent): boolean {
    if (!swiper || !swiper.swiperRef) return true;
    return swiper.swiperRef.activeIndex === 0;
  }

  explore(sportId?: number) {
    const queryParams = sportId ? {sports: sportId} : {};
    this.router.navigate([EVENT_CALENDAR_ROUTE], {queryParams});
  }

  updateSlideCounter(swiper?: SwiperComponent, key?: keyof typeof this.swiperStates) {
    if (!swiper || !swiper.swiperRef || !key) return;

    const slidesPerView = this.getCurrentSlidesPerView();
    const totalSlides = swiper.swiperRef.slides.length - slidesPerView + 1;

    this.swiperStates[key].totalSlides = Math.max(1, Math.ceil(totalSlides));
    this.swiperStates[key].currentSlide = swiper.swiperRef.realIndex + 1;
  }

  onSwiperReady(swiper: any, key: keyof typeof this.swiperStates) {
    swiper.on('slidesLengthChange', () => {
      this.updateSlideCounterFromInstance(swiper, key);
    });

    swiper.on('resize', () => {
      this.updateSlideCounterFromInstance(swiper, key);
    });

    this.updateSlideCounterFromInstance(swiper, key);
  }

  updateSlideCounterFromInstance(swiperInstance: any, key: keyof typeof this.swiperStates) {
    if (!swiperInstance || !key) return;

    const slidesPerView = this.getCurrentSlidesPerView();
    const totalSlides = swiperInstance.slides.length - slidesPerView + 1;

    this.swiperStates[key].totalSlides = Math.max(1, Math.ceil(totalSlides));
    this.swiperStates[key].currentSlide = swiperInstance.realIndex + 1;
  }

  goToFinished() {
    this.router.navigate([this.authenticationService.isAuthenticated() ? PRIVATE_FINISHED_EVENTS_ROUTE : FINISHED_EVENTS_ROUTE]);
  }

  onEventCalendar() {
    this.router.navigate([this.authenticationService.isAuthenticated() ? PRIVATE_EVENT_CALENDAR_ROUTE : EVENT_CALENDAR_ROUTE]);
  }
}
